import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Badge, Button, Dropdown, Field, Input, MessageBar, Option, Spinner,
  Table, TableBody, TableCell, TableHeader, TableHeaderCell, TableRow, Title3,
} from "@fluentui/react-components";
import { api, ApiError } from "../../api/client";
import { useDirectorySearch, useImportUser } from "../../api/hooks";
import type { Role } from "../../types/models";

interface AdminUser {
  id: string; displayName: string; email: string; role: Role;
  isActive: boolean; department?: string | null; jobTitle?: string | null;
}

const ROLES: Role[] = ["EndUser", "Agent", "Admin"];

export function AdminUsersPage() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [dirQ, setDirQ] = useState("");
  const [dirSubmitted, setDirSubmitted] = useState("");
  const list = useQuery({
    queryKey: ["users", q],
    queryFn: () => api.get<AdminUser[]>(`/api/users${q ? `?q=${encodeURIComponent(q)}` : ""}`),
  });
  const setRole = useMutation({
    mutationFn: (input: { id: string; role: Role }) => api.patch(`/api/users/${input.id}/role`, { role: input.role }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
  });
  const setActive = useMutation({
    mutationFn: (input: { id: string; isActive: boolean }) =>
      api.patch(`/api/users/${input.id}/active`, { isActive: input.isActive }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
  });
  const directory = useDirectorySearch(dirSubmitted, !!dirSubmitted);
  const importUser = useImportUser();

  const dirError = directory.error as ApiError | undefined;
  const dirNotConfigured = dirError?.code === "GRAPH_NOT_CONFIGURED";

  return (
    <div>
      <Title3>Users</Title3>

      <h4 style={{ marginTop: 24 }}>Add users from your Microsoft 365 tenant</h4>
      <p style={{ color: "#605E5C", marginTop: 0 }}>
        Search the directory for any user in your tenant. Adding a user creates a local record so
        you can promote them to Agent or Admin <em>before</em> they sign in for the first time.
      </p>
      <div style={{ display: "flex", gap: 8, alignItems: "flex-end", maxWidth: 640 }}>
        <Field label="Tenant search" style={{ flex: 1 }}>
          <Input
            value={dirQ}
            onChange={(_, d) => setDirQ(d.value)}
            onKeyDown={(e) => { if (e.key === "Enter") setDirSubmitted(dirQ.trim()); }}
            placeholder="Name, email, or UPN (e.g. john or john@contoso.com)"
          />
        </Field>
        <Button appearance="primary" onClick={() => setDirSubmitted(dirQ.trim())}>Search directory</Button>
      </div>
      {dirNotConfigured && (
        <MessageBar intent="warning" style={{ marginTop: 8 }}>
          Tenant directory search is disabled because <code>AAD_CLIENT_SECRET</code> isn’t configured,
          or the app registration doesn’t have <code>User.Read.All</code> application permission with admin
          consent. Without this you can only manage users who have already signed in.
        </MessageBar>
      )}
      {dirError && !dirNotConfigured && (
        <MessageBar intent="error" style={{ marginTop: 8 }}>{dirError.message}</MessageBar>
      )}
      {directory.isFetching && <Spinner size="tiny" style={{ marginTop: 8 }} />}
      {directory.data && directory.data.length === 0 && (
        <MessageBar intent="info" style={{ marginTop: 8 }}>No tenant users match “{dirSubmitted}”.</MessageBar>
      )}
      {directory.data && directory.data.length > 0 && (
        <Table size="small" style={{ marginTop: 8 }}>
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Name</TableHeaderCell>
              <TableHeaderCell>Email / UPN</TableHeaderCell>
              <TableHeaderCell>Job / Dept</TableHeaderCell>
              <TableHeaderCell>Status</TableHeaderCell>
              <TableHeaderCell>Add as</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {directory.data.map((u) => (
              <TableRow key={u.aadObjectId}>
                <TableCell>{u.displayName}</TableCell>
                <TableCell>{u.email}</TableCell>
                <TableCell>{[u.jobTitle, u.department].filter(Boolean).join(" — ") || "—"}</TableCell>
                <TableCell>
                  {u.existsLocally
                    ? <Badge appearance="tint" color="success">In app as {u.role}</Badge>
                    : <Badge appearance="tint">Not in app</Badge>}
                </TableCell>
                <TableCell>
                  <div style={{ display: "flex", gap: 4 }}>
                    {ROLES.map((r) => (
                      <Button
                        key={r}
                        size="small"
                        appearance={u.role === r ? "primary" : "secondary"}
                        disabled={importUser.isPending}
                        onClick={() => importUser.mutate(
                          { aadObjectId: u.aadObjectId, role: r },
                          { onSuccess: () => qc.invalidateQueries({ queryKey: ["users", "directory", dirSubmitted] }) },
                        )}
                      >
                        {u.existsLocally && u.role !== r ? `Change to ${r}` : r}
                      </Button>
                    ))}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <h4 style={{ marginTop: 32 }}>Users in this app</h4>
      <Field label="Search" style={{ maxWidth: 360 }}>
        <Input value={q} onChange={(_, d) => setQ(d.value)} placeholder="Name or email" />
      </Field>
      <div style={{ marginTop: 16 }}>
        {list.isLoading ? <Spinner /> : (
          <Table size="small">
            <TableHeader>
              <TableRow>
                <TableHeaderCell>Name</TableHeaderCell>
                <TableHeaderCell>Email</TableHeaderCell>
                <TableHeaderCell>Role</TableHeaderCell>
                <TableHeaderCell>Status</TableHeaderCell>
                <TableHeaderCell>Actions</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(list.data ?? []).map((u) => (
                <TableRow key={u.id}>
                  <TableCell>{u.displayName}</TableCell>
                  <TableCell>{u.email}</TableCell>
                  <TableCell>
                    <Dropdown
                      value={u.role}
                      selectedOptions={[u.role]}
                      onOptionSelect={(_, d) => setRole.mutate({ id: u.id, role: (d.optionValue as Role) ?? u.role })}
                    >
                      {ROLES.map((r) => <Option key={r} value={r}>{r}</Option>)}
                    </Dropdown>
                  </TableCell>
                  <TableCell>
                    <Badge appearance="tint" color={u.isActive ? "success" : "subtle"}>
                      {u.isActive ? "Active" : "Inactive"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Button size="small" onClick={() => setActive.mutate({ id: u.id, isActive: !u.isActive })}>
                      {u.isActive ? "Deactivate" : "Activate"}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
